import { useEffect, useRef } from "react";

export default function InfoSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  /* ── Coordinate grid canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      /* coordinate grid */
      const STEP = 60;
      for (let x = 0; x < W; x += STEP) {
        const pulse = Math.sin(t * 0.008 + x * 0.02) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.strokeStyle = `rgba(255,220,140,${0.012 + pulse * 0.015})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      for (let y = 0; y < H; y += STEP) {
        const pulse = Math.sin(t * 0.007 + y * 0.02) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.strokeStyle = `rgba(255,220,140,${0.008 + pulse * 0.012})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* location pin pulse */
      const px = W * 0.72;
      const py = H * 0.38;
      for (let ring = 0; ring < 3; ring++) {
        const phase = ((t * 0.007 + ring * 0.33) % 1);
        const r = phase * 60;
        const alpha = (1 - phase) * 0.12;
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      /* pin dot */
      ctx.beginPath();
      ctx.arc(px, py, 3, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,220,140,0.5)";
      ctx.fill();

      /* crosshair lines at pin */
      ctx.beginPath();
      ctx.moveTo(px - 12, py);
      ctx.lineTo(px + 12, py);
      ctx.strokeStyle = "rgba(255,220,140,0.2)";
      ctx.lineWidth = 0.5;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(px, py - 12);
      ctx.lineTo(px, py + 12);
      ctx.stroke();

      /* scanning line */
      const scanY = ((t * 1.2) % H);
      const scanGrd = ctx.createLinearGradient(0, scanY - 20, 0, scanY + 20);
      scanGrd.addColorStop(0, "rgba(255,220,140,0)");
      scanGrd.addColorStop(0.5, "rgba(255,220,140,0.04)");
      scanGrd.addColorStop(1, "rgba(255,220,140,0)");
      ctx.fillStyle = scanGrd;
      ctx.fillRect(0, scanY - 20, W, 40);

      t++;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener("resize", resize); };
  }, []);

  /* ── Scroll reveal ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".info-reveal");
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = "1";
                (item as HTMLElement).style.transform = "translateY(0)";
              }, i * 320);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="info"
      ref={sectionRef}
      className="relative w-full flex items-center overflow-hidden"
      style={{ background: "#000000" }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-20 py-24 md:py-44">
        {/* section label */}
        <div className="flex items-center gap-4 mb-16 md:mb-24">
          <div style={{ width: "24px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
          <p className="text-white/15 tracking-[0.5em] uppercase text-xs" style={{ fontFamily: "'Courier New', monospace" }}>
            info
          </p>
        </div>

        <div className="flex flex-col gap-16 md:grid md:grid-cols-2 md:gap-40 items-start md:items-center">

          {/* Left: event info */}
          <div className="flex flex-col gap-10 md:gap-14">

            {/* Date */}
            <div
              className="info-reveal flex flex-col gap-2"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.8s ease, transform 1.8s ease" }}
            >
              <p className="text-white/15 text-[10px] tracking-[0.4em] uppercase mb-1" style={{ fontFamily: "'Courier New', monospace" }}>date</p>
              <p
                className="text-white/70 font-light"
                style={{ fontSize: "clamp(18px, 2.2vw, 28px)", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 300, letterSpacing: "0.08em" }}
              >
                2026.04.10
              </p>
              <p
                className="text-white/25 font-light"
                style={{ fontSize: "clamp(11px, 1.2vw, 15px)", fontFamily: "'Courier New', monospace", letterSpacing: "0.15em" }}
              >
                22:00 — 06:00
              </p>
            </div>

            {/* Location */}
            <div
              className="info-reveal flex flex-col gap-2"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.8s ease, transform 1.8s ease" }}
            >
              <p className="text-white/15 text-[10px] tracking-[0.4em] uppercase mb-1" style={{ fontFamily: "'Courier New', monospace" }}>location</p>
              <p
                className="text-white/70 font-light"
                style={{ fontSize: "clamp(18px, 2.2vw, 28px)", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 300, letterSpacing: "0.08em" }}
              >
                Nishiazabu
              </p>
              <p className="text-white/20 text-xs tracking-widest" style={{ fontFamily: "'Courier New', monospace" }}>
                35.6598° N, 139.7244° E
              </p>
            </div>

            {/* Ticket */}
            <div
              className="info-reveal flex flex-col gap-2"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.8s ease, transform 1.8s ease" }}
            >
              <p className="text-white/15 text-[10px] tracking-[0.4em] uppercase mb-1" style={{ fontFamily: "'Courier New', monospace" }}>ticket</p>
              <p
                className="text-white/70 font-light"
                style={{ fontSize: "clamp(18px, 2.2vw, 28px)", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 300, letterSpacing: "0.08em" }}
              >
                ¥4,000
              </p>
              <p
                className="text-white/20 font-light"
                style={{ fontSize: "clamp(10px, 1vw, 13px)", fontFamily: "'Courier New', monospace", letterSpacing: "0.15em" }}
              >
                20+ only
              </p>
            </div>
          </div>

          {/* Right: map + organizer */}
          <div className="flex flex-col gap-12 md:gap-20">

            {/* Abstract map */}
            <div
              className="info-reveal"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.8s ease, transform 1.8s ease" }}
            >
              <div
                className="relative overflow-hidden"
                style={{
                  width: "min(80vw, 260px)",
                  height: "min(80vw, 260px)",
                  border: "1px solid rgba(255,220,140,0.08)",
                }}
              >
                <img
                  src="https://public.readdy.ai/ai/img_res/b43c360e015d131ece1ca14f1ebd9526.jpg"
                  alt=""
                  className="w-full h-full object-cover opacity-10 grayscale"
                />
                {/* crosshair overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-px h-full bg-white/[0.05] absolute" />
                  <div className="h-px w-full bg-white/[0.05] absolute" />
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      border: "1px solid rgba(255,220,140,0.4)",
                      background: "transparent",
                    }}
                  />
                </div>
                {/* corner marks */}
                {["top-0 left-0", "top-0 right-0", "bottom-0 left-0", "bottom-0 right-0"].map((pos, i) => (
                  <div key={i} className={`absolute ${pos} w-3 h-3 pointer-events-none`}
                    style={{ borderTop: i < 2 ? "1px solid rgba(255,220,140,0.2)" : "none", borderBottom: i >= 2 ? "1px solid rgba(255,220,140,0.2)" : "none", borderLeft: i % 2 === 0 ? "1px solid rgba(255,220,140,0.2)" : "none", borderRight: i % 2 === 1 ? "1px solid rgba(255,220,140,0.2)" : "none" }}
                  />
                ))}
                {/* coordinate label */}
                <div className="absolute bottom-2 left-2">
                  <p className="text-white/15 text-[8px] tracking-widest" style={{ fontFamily: "'Courier New', monospace" }}>NW.AZB</p>
                </div>
              </div>
            </div>

            {/* Organizer */}
            <div
              className="info-reveal flex flex-col gap-2"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.8s ease, transform 1.8s ease" }}
            >
              <p className="text-white/15 text-[10px] tracking-[0.4em] uppercase mb-1" style={{ fontFamily: "'Courier New', monospace" }}>organizer</p>
              <div className="flex items-center gap-3">
                <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: "rgba(255,220,140,0.4)" }} />
                <p
                  className="text-white/45 font-light"
                  style={{ fontSize: "clamp(13px, 1.6vw, 20px)", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 300, letterSpacing: "0.08em" }}
                >
                  Karen Tam
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
